// Backend API Service
// Handles communication with the secure backend API for email processing

/**
 * Backend API Service for secure email processing
 * This service calls the backend API which handles token decryption and N8N webhook calls
 */
class BackendApiService {
  constructor() {
    this.baseUrl = '/api/email-scan'; // Uses Vite proxy in development
    this.timeout = 30000; // 30 seconds
  }

  /**
   * Trigger email scan through secure backend
   * @param {string} userId - User ID
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} API response
   */
  async triggerEmailScan(userId, options = {}) {
    const {
      processing_type = 'historical',
      use_test_webhook = false,
      ...restOptions
    } = options;

    console.log('[BackendApi] Triggering email scan for user:', userId);
    console.log('[BackendApi] Options:', { processing_type, use_test_webhook, ...restOptions });

    try {
      // Build request payload - include options object for N8N workflow
      const requestPayload = {
        user_id: userId,
        processing_type,
        use_test_webhook
      };
      
      // Include additional options if provided (for future backend/N8N use)
      if (Object.keys(restOptions).length > 0) {
        requestPayload.options = restOptions;
      }

      const response = await fetch(`${this.baseUrl}/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
        timeout: this.timeout
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[BackendApi] Email scan triggered successfully:', result);
      
      return result;

    } catch (error) {
      console.error('[BackendApi] Error triggering email scan:', error);
      
      // Provide helpful error message if backend server isn't running
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('Load failed') || 
          error.message.includes('NetworkError') ||
          error.name === 'TypeError') {
        const helpfulError = new Error(
          'Backend server is not running. Please start it with: cd backend && npm start'
        );
        helpfulError.originalError = error;
        throw helpfulError;
      }
      
      throw error;
    }
  }

  /**
   * Get backend API status and configuration
   * @returns {Promise<Object>} Status information
   */
  async getStatus() {
    try {
      const response = await fetch(`${this.baseUrl}/status`, {
        method: 'GET',
        timeout: 10000 // 10 seconds for status check
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const status = await response.json();
      console.log('[BackendApi] Status retrieved:', status);
      
      return status;

    } catch (error) {
      console.error('[BackendApi] Error getting status:', error);
      throw error;
    }
  }

  /**
   * Test backend connectivity
   * @returns {Promise<boolean>} True if backend is reachable
   */
  async testConnectivity() {
    try {
      const status = await this.getStatus();
      return status.success === true;
    } catch (error) {
      console.error('[BackendApi] Connectivity test failed:', error);
      return false;
    }
  }
}

// Create singleton instance
const backendApiService = new BackendApiService();

// Export for use in components
export default backendApiService;

// Export individual functions for convenience
export const triggerEmailScan = (userId, options) => backendApiService.triggerEmailScan(userId, options);
export const getBackendStatus = () => backendApiService.getStatus();
export const testBackendConnectivity = () => backendApiService.testConnectivity();
